// Field tooltip descriptions based on Business Use from Syrinx_CSV_Import_Column_Descriptions.md

export const fieldTooltips = {
  // Fleet Reminders fields
  stock_no: 'Used for hire contract creation, availability tracking, and equipment management',
  service_name: 'Organizes reminders by type for different maintenance activities',
  due_date: 'Used for scheduling and alert generation',
  due_meter: 'Used for scheduling and alert generation based on equipment usage',
  due_notes: 'Provides specific information about the required action',
  inspection_name: 'Used in reminder categorization and scheduling systems',
  last_test_date: 'Used for tracking previous maintenance and scheduling',
  last_test_no: 'Used for tracking previous maintenance and scheduling',
  during_hire_only: 'Allows deactivation of reminders without deletion',

  // Inspection Tests fields
  inspection_name: 'Used to define standard inspection and testing procedures',
  expiry_days: 'Defines how often the test should be performed (in days)',
  print_on_confirm: 'Controls automatic printing of test results',
  raise_job: 'Determines if maintenance jobs should be created for failed tests',
  item_name: 'Used for detailed specifications and maintenance',
  check_for: 'Provides detailed information about how the test should be performed',
  requires_value: 'Determines if the test requires numerical measurement',
  low_value: 'Minimum acceptable value for the test measurement',
  high_value: 'Maximum acceptable value for the test measurement',
  value_units: 'Units of measurement for test values (e.g., Hours, PSI, °C)',
  print: 'Controls whether test results are printed automatically',
  help_notes: 'Provides additional guidance for performing the test',

  // Inclusions fields
  parent_type: 'Determines whether inclusion applies to hire categories (C) or stock numbers (S)',
  parent_code: 'Links inclusions to specific hire categories or stock items',
  incl_cat: 'Associates inclusion with equipment category for easier management',
  incl_stockno: 'Specific stock item to be included with the parent equipment',
  order_num: 'Controls the display order of inclusions in lists and reports',
  quantity: 'Number of items to be included with the parent equipment',
  include_flag: 'Controls whether the inclusion is active and should be included',
  no_charge: 'Determines if the inclusion is provided at no additional cost',
  sales_inclusion_method: 'Method for including items in sales transactions',

  // Hire Categories fields
  code: 'Used in equipment classification, pricing, and hire contract management',
  name: 'Used in user interfaces, reports, and equipment selection',
  hire_cat_group: 'Provides hierarchical organization of hire categories',
  current_flag: 'Allows deactivation of categories without deletion',
  nc_hire: 'Nominal code for hire revenue (Sales type)',
  nc_operated: 'Nominal code for operated hire (Sales type)',
  nc_sales: 'Nominal code for sales transactions (Sales type)',
  nc_purchase: 'Nominal code for purchase transactions (Purchase type)',

  // Hire Stock Individual fields
  main_stock_number:
    'Used for hire contract creation, availability tracking, and equipment management',
  serial_no: 'Used for warranty tracking, service history, and asset identification',
  item_name: 'Used in equipment selection, contracts, and customer-facing documents',
  purchase_price: 'Used for depreciation calculations, insurance, and asset valuation',
  purchase_date: 'Used for depreciation calculations, warranty tracking, and age analysis',
  hire_rate_per_day: 'Used in hire contract pricing and revenue calculations',
  hire_rate_per_week: 'Used for longer-term hire contracts and pricing options',
  hire_rate_per_month: 'Used for monthly hire contracts and pricing options',
  depot_id: 'Used for availability checking, delivery planning, and equipment tracking',
  make: 'Used for parts sourcing, maintenance procedures, and equipment categorization',
  model: 'Used for detailed specifications, parts sourcing, and maintenance',
  current_flag: 'Controls whether equipment is available for hire',

  // Sales Categories fields
  sales_category_code: 'Used in sales item classification, pricing, and inventory management',
  sales_category_name: 'Used in user interfaces, reports, and item selection',
  sales_current_flag: 'Allows deactivation of categories without deletion',

  // Sales Items fields
  main_stock_number: 'Used for sales transactions, inventory tracking, and item management',
  sales_serial_no: 'Used for warranty tracking and individual item identification',
  sales_item_name: 'Used in sales catalogs, invoices, and customer-facing documents',
  sales_purchase_price: 'Used for profit margin analysis and pricing decisions',
  sales_purchase_date: 'Used for inventory aging and cost analysis',
  sale_price: 'Used in sales transactions and pricing',
  sales_depot_id: 'Used for inventory tracking and sales fulfillment',
  sales_make: 'Used for product categorization and customer selection',
  sales_model: 'Used for detailed product specifications and selection',
  create_inspections_flag: 'Determines if inspection schedules should be created for the item',

  // Staff Members fields
  short_name: 'Used in user interfaces and quick staff identification',
  first_name: 'Used in staff records and communications',
  surname: 'Used for staff identification and records',
  employee_no: 'Used for payroll integration and staff tracking',
  hire_desk_role: 'Controls access to hire desk functions and reporting',
  driver_role: 'Used for delivery scheduling and driver assignment',
  operator_role: 'Used for equipment operation assignments and scheduling',
  workshop_role: 'Used for workshop job assignments and scheduling',
  sales_role: 'Used for sales assignment and commission tracking',
  purchasing_role: 'Used for purchase order approval and procurement',
  notes: 'Used for additional staff information and comments',
  cost_per_hour: 'Used for job costing and profitability analysis',
  workshop_recharge_per_hour: 'Used for workshop job pricing and billing',
  default_depot_id: 'Used for job assignment and location-based operations',

  // Customer Sites fields
  account_no: 'Links sites to customer records for delivery and service',
  site_name: 'Used for site selection in deliveries and service calls',
  address_line_1: 'Used for delivery and service visit navigation',
  address_line_2: 'Provides additional address detail for accurate delivery',
  address_line_3: 'Provides extra address detail if needed',
  town: 'Used for delivery routing and geographic reporting',
  county: 'Used for regional reporting and delivery planning',
  postcode: 'Used for delivery routing and geographic analysis',
  telephone: 'Used for delivery coordination and site contact',
  fax: 'Used for document transmission to the site',
  delivery_directions: 'Provides drivers with detailed delivery instructions',
  start_date: 'Controls when the site is available for deliveries',
  end_date: 'Controls site availability for time-limited projects',
  latitude: 'Used for GPS navigation and delivery routing',
  longitude: 'Used for GPS navigation and delivery routing',
  area: 'Used for delivery routing and regional management',
  route: 'Used for delivery planning and route optimization',
  depot_id: 'Determines which depot handles deliveries and service',

  // Customer fields
  name: 'Used in customer records and communications',
  account_no: 'Used for customer identification and account management',
  line_1: 'Used for delivery and service visit navigation',
  line_2: 'Provides additional address detail for accurate delivery',
  line_3: 'Provides extra address detail if needed',
  town: 'Used for delivery routing and geographic reporting',
  county: 'Used for regional reporting and delivery planning',
  postcode: 'Used for delivery routing and geographic analysis',
  country: 'Used for international delivery and compliance',
  telephone: 'Used for customer communication and coordination',
  fax: 'Used for document transmission to the customer',
  contact_name: 'Used for customer communication and coordination',
  contact_telephone: 'Used for customer communication and coordination',
  email: 'Used for customer communication and coordination',
  cash_ident: 'Used for cash customer identification and processing',
  type: 'Used for customer categorization and reporting',
  cat_1: 'Used for customer categorization and reporting',
  cat_2: 'Used for customer categorization and reporting',
  cat_3: 'Used for customer categorization and reporting',
  cat_4: 'Used for customer categorization and reporting',
  cat_5: 'Used for customer categorization and reporting',
  cat_6: 'Used for customer categorization and reporting',
  size: 'Used for customer categorization and reporting',
  how_heard_of_us: 'Used for marketing analysis and customer acquisition tracking',
  alert_text: 'Used for customer-specific alerts and notifications',
  company_type: 'Used for customer categorization and reporting',
  website: 'Used for customer communication and marketing',
  notes: 'Used for additional customer information and comments',
  on_hold: 'Controls whether customer account is active',
  company_reg_no: 'Used for legal compliance and customer verification',
  company_vat_no: 'Used for tax compliance and customer verification',
  company_cis_reg_no: 'Used for construction industry scheme compliance',
  default_hire_period: 'Used for default contract terms and pricing',
  depot: 'Used for customer assignment and service delivery',
  tax_code: 'Used for tax calculation and compliance',
  parent_acc_no: 'Used for customer hierarchy and account management',
  parent_id: 'Used for customer hierarchy and account management',
  is_internal: 'Used for internal customer identification and processing',
  alt_name: 'Used for alternative customer identification',
  active: 'Controls whether customer account is active',

  // Supplier fields
  supplier_name: 'Used in supplier records and communications',
  supplier_account_no: 'Used for supplier identification and account management',
  supplier_line_1: 'Used for delivery and service visit navigation',
  supplier_line_2: 'Provides additional address detail for accurate delivery',
  supplier_line_3: 'Provides extra address detail if needed',
  supplier_town: 'Used for delivery routing and geographic reporting',
  supplier_county: 'Used for regional reporting and delivery planning',
  supplier_postcode: 'Used for delivery routing and geographic analysis',
  supplier_country: 'Used for international delivery and compliance',
  supplier_telephone: 'Used for supplier communication and coordination',
  supplier_fax: 'Used for document transmission to the supplier',
  supplier_contact_name: 'Used for supplier communication and coordination',
  supplier_contact_telephone: 'Used for supplier communication and coordination',
  supplier_email: 'Used for supplier communication and coordination',
  supplier_type: 'Used for supplier categorization and reporting',
  supplier_website: 'Used for supplier communication and marketing',
  supplier_notes: 'Used for additional supplier information and comments',
  supplier_on_hold: 'Controls whether supplier account is active',
  supplier_company_reg_no: 'Used for legal compliance and supplier verification',
  supplier_company_vat_no: 'Used for tax compliance and supplier verification',
  supplier_active: 'Controls whether supplier account is active',

  // Hire Category Groups fields
  group_name: 'Used to organize hire equipment into logical groups for pricing and reporting',
  description: "Provides additional information about the group's purpose and contents",
  current_flag: 'Allows deactivation of groups without deletion, maintaining data integrity',

  // Nominal Codes fields
  nominal_code: 'Used in financial transactions, reporting, and accounting integration',
  nominal_name: 'Used in financial reports and accounting interfaces',
};

// Helper function to get tooltip text for a field
export const getFieldTooltip = (fieldName) => {
  return fieldTooltips[fieldName] || 'No description available';
};

// Helper function to create Bootstrap tooltip props
export const createTooltipProps = (fieldName, placement = 'top') => {
  const tooltipText = getFieldTooltip(fieldName);
  return {
    'data-bs-toggle': 'tooltip',
    'data-bs-placement': placement,
    'data-bs-title': tooltipText,
    'data-bs-html': 'true',
  };
};
